/**
 * Médicaments (référentiel) — DCI/INN Maroc complet
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Pill, Search, AlertTriangle, Baby, DollarSign, BookOpen, ShieldAlert } from "lucide-react";
import { MEDICATIONS, ATC_CLASSES, ALERT_TONE, PREGNANCY_LABEL, type Drug } from "@/lib/referential/medications";

export const Route = createFileRoute("/referential/medications")({
  component: MedicationsReferential,
});

function MedicationsReferential() {
  const [search, setSearch] = useState("");
  const [atcFilter, setAtcFilter] = useState<string | "all">("all");
  const [alertFilter, setAlertFilter] = useState<"all" | Drug["alertLevel"]>("all");
  const [selected, setSelected] = useState<Drug | null>(null);

  const filtered = useMemo(() => {
    return MEDICATIONS.filter((m) => {
      if (atcFilter !== "all" && !m.atcCode.startsWith(atcFilter)) return false;
      if (alertFilter !== "all" && m.alertLevel !== alertFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        m.inn.toLowerCase().includes(s) ||
        m.atcCode.toLowerCase().includes(s) ||
        m.brandNames.some((b) => b.toLowerCase().includes(s)) ||
        m.form.toLowerCase().includes(s)
      );
    });
  }, [search, atcFilter, alertFilter]);

  const stats = useMemo(() => {
    const total = MEDICATIONS.length;
    const generics = MEDICATIONS.filter((m) => m.genericAvailable).length;
    const controlled = MEDICATIONS.filter((m) => m.controlled).length;
    const critical = MEDICATIONS.filter((m) => m.alertLevel === "CRITICAL").length;
    return { total, generics, controlled, critical };
  }, []);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Pill className="h-5 w-5" /> Référentiel médicaments (DCI/INN)
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Base nationale Maroc · INN · ATC · Génériques · Restrictions
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Total médicaments</div>
            <div className="mt-1 text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Génériques dispo</div>
            <div className="mt-1 text-2xl font-bold text-emerald-400">{stats.generics}</div>
            <div className="text-xs text-muted-foreground">{Math.round((stats.generics / stats.total) * 100)}% du catalogue</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Stupéfiants</div>
            <div className="mt-1 text-2xl font-bold text-red-400">{stats.controlled}</div>
            <div className="text-xs text-muted-foreground">ordonnance sécurisée</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Alerte critique</div>
            <div className="mt-1 text-2xl font-bold text-amber-400">{stats.critical}</div>
            <div className="text-xs text-muted-foreground">vérification obligatoire</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (DCI, marque, ATC, forme)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          size="sm"
          variant={alertFilter === "CRITICAL" ? "destructive" : "outline"}
          onClick={() => setAlertFilter(alertFilter === "CRITICAL" ? "all" : "CRITICAL")}
        >
          <ShieldAlert className="h-3.5 w-3.5 mr-1" />
          Critiques seulement
        </Button>
      </div>

      <Tabs value={atcFilter} onValueChange={(v) => setAtcFilter(v as any)}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="all" className="text-xs">Tous ({MEDICATIONS.length})</TabsTrigger>
          {ATC_CLASSES.map((c) => (
            <TabsTrigger key={c.code} value={c.code} className="text-xs">
              {c.code} · {c.name.length > 24 ? c.name.slice(0, 24) + "…" : c.name} ({MEDICATIONS.filter((m) => m.atcCode.startsWith(c.code)).length})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((m) => (
          <Card
            key={m.id}
            className={`cursor-pointer transition hover:border-primary/30 ${selected?.id === m.id ? "border-primary" : ""}`}
            onClick={() => setSelected(m)}
          >
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium truncate">{m.inn}</span>
                    {m.controlled && <Badge variant="destructive" className="text-[9px]">STUP</Badge>}
                    <Badge variant={ALERT_TONE[m.alertLevel]} className="text-[9px]">{m.alertLevel}</Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {m.dosage} · {m.form} · {m.atcCode}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground truncate">
                    {m.brandNames.slice(0, 3).join(", ")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold">{m.priceMAD} MAD</div>
                  <div className="text-[10px] text-muted-foreground">RBT {m.reimbursement}%</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground">
            Aucun médicament ne correspond
          </div>
        )}
      </div>

      {/* Selected drug detail */}
      {selected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {selected.inn} {selected.dosage} {selected.form}
            </CardTitle>
            <CardDescription>
              <code>{selected.atcCode}</code> · Marques : {selected.brandNames.join(", ")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4 text-sm">
              <Field icon={<DollarSign className="h-3 w-3" />} label="Prix" value={`${selected.priceMAD} MAD`} />
              <Field icon={<BookOpen className="h-3 w-3" />} label="Remboursement" value={`${selected.reimbursement}%`} />
              <Field icon={<Baby className="h-3 w-3" />} label="Grossesse" value={PREGNANCY_LABEL[selected.pregnancyCategory]} />
              <Field icon={<Pill className="h-3 w-3" />} label="Générique" value={selected.genericAvailable ? "Oui" : "Non"} />
            </div>
            {selected.contraindications.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Contre-indications</div>
                <div className="flex flex-wrap gap-1">
                  {selected.contraindications.map((c) => (
                    <Badge key={c} variant="destructive" className="text-[10px]">
                      <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                      {c}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {selected.interactions.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Interactions ({selected.interactions.length})</div>
                <div className="flex flex-wrap gap-1">
                  {selected.interactions.map((i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">{i}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground flex items-center gap-1">{icon} {label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
